/* CCS UX/UI Audit enhancements — May 4, 2026
   - Promote SKU + part number on PLP cards
   - Render lead-time pill on OOS cards (reads metafield via product handle JSON fetch, cached)
   - Update header search placeholder to industrial copy
   - Inject quick-add stepper on PLP cards
   - Tag homepage product carousels with .home-grid-mode for grid-fallback CSS
*/
(function () {
  "use strict";

  // ---- 1. Header search placeholder ----
  function updateSearchPlaceholder() {
    var inputs = document.querySelectorAll(
      '#header-global-search-input, input[type="search"][name="q"]'
    );
    inputs.forEach(function (i) {
      i.setAttribute("placeholder", "Search part #, model #, or keyword");
    });
  }

  // ---- 1a0. Relocate phone + Customer Support promo block to the right side ----
  // The theme renders it in the mid-slot next to search; we want it next to
  // the account/cart icons. Clone (not move) so design-mode editing of the
  // theme settings still updates a real DOM node.
  function relocatePromoBlock() {
    var src = document.querySelector(
      ".head-slot-mid .header-brand-info"
    );
    var endSlot = document.querySelector(".head-slot-end .head-slot-item-icons");
    if (!src || !endSlot) return;
    if (document.querySelector(".head-slot-end .header-brand-info")) return;
    var clone = src.cloneNode(true);
    clone.classList.add("header-brand-info--relocated");
    endSlot.parentElement.insertBefore(clone, endSlot);
  }

  // ---- 1b. Rename "All Parts" → "Browse" in main nav (dev-theme only) ----
  // Menu link names are global (live across themes), so rename via DOM here
  // instead of editing Shopify Navigation. Promote to a real menu rename
  // before publishing this theme.
  function renameNavAllParts() {
    var labels = document.querySelectorAll(
      ".main-nav-list a, .head-nav a, [class*='main-nav'] a, .megamenu-trigger, .main-nav-link"
    );
    labels.forEach(function (a) {
      // Walk text nodes to avoid clobbering badges/icons/child elements
      a.childNodes.forEach(function (n) {
        if (n.nodeType === 3 && n.textContent.trim().toLowerCase() === "all parts") {
          n.textContent = n.textContent.replace(/all parts/i, "Browse");
        }
      });
      // Fallback: single-text-child link
      if (
        a.children.length === 0 &&
        a.textContent.trim().toLowerCase() === "all parts"
      ) {
        a.textContent = "Browse";
      }
    });
  }

  // ---- 2. Promote SKU on PLP cards ----
  // The card snippet only renders SKU when show_sku flag is set (it isn't).
  // We pull SKU from product JSON via the card link and inject visibly.
  var skuCache = new Map();
  function promoteSKU() {
    var cards = document.querySelectorAll(
      "product-card .card-info, .card.card-product .card-info"
    );
    cards.forEach(function (card) {
      if (card.dataset.skuPromoted) return;
      // Skip if SKU already rendered by snippet
      if (card.querySelector("[data-card-sku], .ccs-card-sku")) {
        card.dataset.skuPromoted = "1";
        return;
      }
      var link = card
        .closest("product-card, .card-product")
        ?.querySelector('a[href*="/products/"]');
      if (!link) return;
      var url = link.getAttribute("href").split("?")[0];
      if (skuCache.has(url)) {
        injectSKU(card, skuCache.get(url));
        card.dataset.skuPromoted = "1";
        return;
      }
      // Defer fetch to idle to avoid blocking
      var doFetch = function () {
        fetch(url + ".json", { credentials: "same-origin" })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            var sku = d && d.product && d.product.variants && d.product.variants[0]
              ? d.product.variants[0].sku
              : null;
            skuCache.set(url, sku);
            if (sku) injectSKU(card, sku);
            card.dataset.skuPromoted = "1";
          })
          .catch(function () { card.dataset.skuPromoted = "1"; });
      };
      if (window.requestIdleCallback) requestIdleCallback(doFetch, { timeout: 1500 });
      else setTimeout(doFetch, 100);
    });
  }
  function injectSKU(card, sku) {
    if (!sku) return;
    var eyebrow = card.querySelector(".card-eyebrow");
    if (!eyebrow) {
      // create one
      eyebrow = document.createElement("div");
      eyebrow.className = "card-eyebrow";
      card.insertBefore(eyebrow, card.firstChild);
    }
    if (eyebrow.querySelector(".ccs-card-sku")) return;
    var span = document.createElement("span");
    span.className = "card-eyebrow-item ccs-card-sku";
    span.textContent = sku;
    eyebrow.appendChild(span);
  }

  // ---- 3. Lead-time pill on OOS cards ----
  // Reads product metafield custom.lead_time_days via the storefront product JSON.
  function renderLeadTime() {
    var oosCards = document.querySelectorAll("product-card, .card.card-product");
    oosCards.forEach(function (card) {
      if (card.dataset.leadTimeChecked) return;
      var stockBadge = card.querySelector(".badge-stock-out, [class*='out_of_stock'], [class*='out-of-stock']");
      if (!stockBadge) return;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      var url = link.getAttribute("href").split("?")[0];
      var doFetch = function () {
        fetch(url + ".json", { credentials: "same-origin" })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            card.dataset.leadTimeChecked = "1";
            // Storefront product JSON does not expose metafields by default.
            // Fallback: pull from data attribute if theme renders one, otherwise show generic.
            var days = card.getAttribute("data-lead-time-days") || null;
            var pill = document.createElement("span");
            pill.className = "ccs-lead-time";
            pill.textContent = days
              ? "Ships in ~" + days + " days"
              : "Backordered — call for ETA";
            var footerIntro = card.querySelector(".card-footer-intro");
            if (footerIntro) footerIntro.appendChild(pill);
          })
          .catch(function () { card.dataset.leadTimeChecked = "1"; });
      };
      if (window.requestIdleCallback) requestIdleCallback(doFetch, { timeout: 2000 });
      else setTimeout(doFetch, 200);
    });
  }

  // ---- 4. Quick-add stepper ----
  function injectQuickAdd() {
    var cards = document.querySelectorAll("product-card, .card.card-product");
    cards.forEach(function (card) {
      if (card.dataset.qaInjected) return;
      // skip if OOS (no point)
      if (card.querySelector(".badge-stock-out")) return;
      var footer = card.querySelector(".card-footer");
      if (!footer) return;
      // Skip if PLP only — keep existing add-to-cart on featured collections in place
      if (!card.closest(".product-grid-template, .collection-product-grid, .main-collection-product-grid")) {
        return;
      }
      var existingAtc = card.querySelector("form[action*='/cart/add'], .product-actions");
      var idInput =
        (existingAtc && existingAtc.querySelector('input[name="id"]')) || null;
      if (!idInput) return;
      var variantId = idInput.value;
      var wrap = document.createElement("div");
      wrap.className = "ccs-qty-add";
      wrap.innerHTML =
        '<button type="button" class="ccs-qty-step" data-step="-1" aria-label="Decrease">−</button>' +
        '<input type="number" class="ccs-qty-input" min="1" value="1" aria-label="Quantity">' +
        '<button type="button" class="ccs-qty-step" data-step="1" aria-label="Increase">+</button>' +
        '<button type="button" class="ccs-qty-add-btn" data-variant="' +
        variantId +
        '">Add</button>';
      footer.appendChild(wrap);
      card.dataset.qaInjected = "1";
    });
  }

  document.addEventListener("click", function (e) {
    var t = e.target;
    if (t.classList && t.classList.contains("ccs-qty-step")) {
      var input = t.parentElement.querySelector(".ccs-qty-input");
      var step = parseInt(t.dataset.step, 10) || 1;
      var v = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      input.value = v;
    } else if (t.classList && t.classList.contains("ccs-qty-add-btn")) {
      var qty = parseInt(t.parentElement.querySelector(".ccs-qty-input").value, 10) || 1;
      var variantId = t.dataset.variant;
      t.disabled = true;
      var origText = t.textContent;
      t.textContent = "···";
      fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          t.textContent = "✓ Added";
          // Trigger theme cart refresh if available
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          window.dispatchEvent(new CustomEvent("ajaxProduct:added"));
          setTimeout(function () {
            t.textContent = origText;
            t.disabled = false;
          }, 1400);
        })
        .catch(function () {
          t.textContent = "Error";
          setTimeout(function () {
            t.textContent = origText;
            t.disabled = false;
          }, 1400);
        });
    }
  });

  // ---- 5. Tag homepage carousels for grid-fallback CSS ----
  function tagHomeGridMode() {
    if (!document.body.classList.contains("template-index")) return;
    document
      .querySelectorAll(
        '[data-section-type="featured-collection"], [data-section-type="collection-tabs"]'
      )
      .forEach(function (s) { s.classList.add("home-grid-mode"); });
  }

  function run() {
    updateSearchPlaceholder();
    relocatePromoBlock();
    renameNavAllParts();
    tagHomeGridMode();
    promoteSKU();
    renderLeadTime();
    injectQuickAdd();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
  // Re-run on AJAX collection updates (filters)
  document.addEventListener("collection:updated", run);
  document.addEventListener("section:loaded", run);
  // MutationObserver fallback for filtered grids
  var grid = document.querySelector(
    ".product-grid-template, .collection-product-grid, .main-collection-product-grid"
  );
  if (grid && window.MutationObserver) {
    new MutationObserver(function () {
      promoteSKU();
      renderLeadTime();
      injectQuickAdd();
    }).observe(grid, { childList: true, subtree: true });
  }
})();
